import type { EquipmentConfig } from '../AppearanceDefinitions';
import type { ResolvedPlayerEquipmentVisuals, ItemEquipmentVisualDescriptor } from '../../../shared/equipmentVisuals';
import { attachmentAnchorsForPose } from './AttachmentGeometry';
import type { AttachmentAnchor, GhostPoseAnchors } from './AttachmentGeometry';
import {
  APPENDAGE_GEOMETRY,
  APPENDAGE_STYLE,
  DEFAULT_GEAR_FIT,
  EMPTY_EQUIPMENT_SLOTS,
  RESOLVED_APPENDAGE_FAMILIES,
  darken,
  normalizeGearVariant,
  resolveAppendageVariantScale,
  resolveLimbGearScale,
} from './GearGeometry';
import type { GearFitContext, GearFitLayer } from './GearGeometry';
import {
  renderGearSilhouettes,
  renderResolvedSlotSilhouette,
  renderTorsoSleeves,
} from './GearSilhouettes';
import type { TorsoFamily } from './GearRecipes';

export type { GearFitContext } from './GearGeometry';

export interface GearLayers {
  /** Capes and robe mantles behind the body. */
  back: string;
  /** Boots attached to the animated feet. */
  feet: string;
  /** Glove attached to the rear arm, still behind the torso. */
  backHand: string;
  /** Garment coverage following the animated rear arm. */
  backArm: string;
  /** Chest, shoulders, legs, belt, neck, and class-item layers. */
  body: string;
  /** Garment coverage following the animated leading arm. */
  frontArm: string;
  /** Glove attached to the leading hand. */
  frontHand: string;
  /** Helmet or equipment hood over the face. */
  head: string;
}

type AppendageKind = 'glove' | 'boot';
type AppendageName = 'back-hand' | 'front-hand' | 'back-foot' | 'front-foot';

function renderAnchoredAppendage(
  kind: AppendageKind,
  name: AppendageName,
  anchor: AttachmentAnchor,
  equipment: EquipmentConfig,
  scale: number,
  opacity = 1,
): string {
  const path = APPENDAGE_GEOMETRY[equipment.type][kind];
  const highlightPath = kind === 'glove'
    ? APPENDAGE_STYLE.gloveHighlightPath
    : APPENDAGE_STYLE.bootHighlightPath;
  const highlightOpacity = kind === 'glove'
    ? APPENDAGE_STYLE.gloveHighlightOpacity
    : APPENDAGE_STYLE.bootHighlightOpacity;

  return `<g data-gear-anchor="${name}" data-anchor-x="${anchor.x}" data-anchor-y="${anchor.y}" data-gear-scale="${scale}" transform="translate(${anchor.x} ${anchor.y}) rotate(${anchor.angle}) scale(${scale})" opacity="${opacity}">
    <path d="${path}" fill="${equipment.color}" stroke="${APPENDAGE_STYLE.outline}" stroke-width="${APPENDAGE_STYLE.outlineWidth}" />
    <path d="${highlightPath}" fill="none" stroke="${APPENDAGE_STYLE.highlight}" stroke-width="${APPENDAGE_STYLE.highlightWidth}" opacity="${highlightOpacity}" />
    <circle data-gear-anchor-point="${name}" cx="0" cy="0" r="0" />
  </g>`;
}

/**
 * Render an anchored appendage (boot/glove) from a resolved descriptor.
 * Uses the descriptor's primaryColor and includes semantic data attributes.
 * The geometry path comes from the family registry, not a global armor type.
 */
function renderResolvedAppendage(
  kind: AppendageKind,
  name: AppendageName,
  anchor: AttachmentAnchor,
  d: ItemEquipmentVisualDescriptor,
  geometryPath: string,
  scale: number,
  opacity = 1,
): string {
  const highlightPath = kind === 'glove'
    ? APPENDAGE_STYLE.gloveHighlightPath
    : APPENDAGE_STYLE.bootHighlightPath;
  const highlightOpacity = kind === 'glove'
    ? APPENDAGE_STYLE.gloveHighlightOpacity
    : APPENDAGE_STYLE.bootHighlightOpacity;
  const accent = d.accentColor ?? d.primaryColor;

  return `<g data-gear-anchor="${name}" data-anchor-x="${anchor.x}" data-anchor-y="${anchor.y}" data-gear-scale="${scale}" data-gear-fit-mode="anchored" data-gear-variant-tier="${normalizeGearVariant(d.variant)}" data-equipment-slot="${d.schemaSlot}" data-equipment-item-id="${d.itemId}" data-equipment-family="${d.family}" data-equipment-variant="${d.variant}" transform="translate(${anchor.x} ${anchor.y}) rotate(${anchor.angle}) scale(${scale})" opacity="${opacity}">
    <path d="${geometryPath}" fill="${d.primaryColor}" stroke="${APPENDAGE_STYLE.outline}" stroke-width="${APPENDAGE_STYLE.outlineWidth}" />
    <path d="${highlightPath}" fill="none" stroke="${accent}" stroke-width="${APPENDAGE_STYLE.highlightWidth}" opacity="${highlightOpacity}" />
    <circle data-gear-anchor-point="${name}" cx="0" cy="0" r="0" />
  </g>`;
}

function fittedLayer(
  content: string,
  layer: GearFitLayer,
  context: GearFitContext,
): string {
  if (!content) return '';
  return `<g data-gear-fit-root="${layer}" data-gear-shape="${context.shape}" data-gear-size="${context.bodySize}" data-gear-fit-strategy="body-rig">${content}</g>`;
}

function legacyTorsoFamily(
  equipment: EquipmentConfig,
  chestVariant: number,
): TorsoFamily {
  if (equipment.type === 'plate') return chestVariant === 1 ? 'plate' : 'mail';
  if (equipment.type === 'robe') return 'robe';
  return 'tunic';
}

/**
 * Compose semantic paper-doll layers around canonical animated anchors.
 * Material geometry and body-fit policy live in independent typed modules so
 * new armor families and body sizes do not expand this render orchestrator.
 */
export class GearParts {
  static renderLayers(
    equipment: EquipmentConfig,
    anchors: GhostPoseAnchors = attachmentAnchorsForPose('idle'),
    context: GearFitContext = DEFAULT_GEAR_FIT,
  ): GearLayers {
    const slots = equipment.slots ?? EMPTY_EQUIPMENT_SLOTS;
    const silhouettes = renderGearSilhouettes(equipment, context);
    const limbScale = resolveLimbGearScale(context);
    const sleeves = slots.chest > 0
      ? renderTorsoSleeves(
        legacyTorsoFamily(equipment, slots.chest),
        equipment.color,
        darken(equipment.color, 0.68),
        slots.chest - 1,
        context,
        anchors,
      )
      : { back: '', front: '' };

    return {
      back: fittedLayer(silhouettes.back, 'back', context),
      feet: slots.feet > 0
        ? renderAnchoredAppendage(
          'boot',
          'back-foot',
          anchors.backFoot,
          equipment,
          limbScale,
          APPENDAGE_STYLE.backFootOpacity,
        ) + renderAnchoredAppendage('boot', 'front-foot', anchors.frontFoot, equipment, limbScale)
        : '',
      backHand: slots.hands > 0
        ? renderAnchoredAppendage(
          'glove',
          'back-hand',
          anchors.backHand,
          equipment,
          limbScale,
          APPENDAGE_STYLE.backHandOpacity,
        )
        : '',
      backArm: sleeves.back,
      body: fittedLayer(silhouettes.body, 'body', context),
      frontArm: sleeves.front,
      frontHand: slots.hands > 0
        ? renderAnchoredAppendage('glove', 'front-hand', anchors.frontHand, equipment, limbScale)
        : '',
      head: fittedLayer(silhouettes.head, 'head', context),
    };
  }

  /** Compatibility helper for standalone previews; GhostRenderer uses layers. */
  static render(equipment: EquipmentConfig): string {
    const layers = GearParts.renderLayers(
      equipment,
      attachmentAnchorsForPose('idle'),
      DEFAULT_GEAR_FIT,
    );
    return `<g>${layers.back}${layers.feet}${layers.backArm}${layers.backHand}${layers.body}${layers.frontArm}${layers.frontHand}${layers.head}</g>`;
  }

  // ─── Resolved-descriptor rendering (BIG-EQUIP-VIS-01 S2) ──────

  /**
   * Render per-slot gear from resolved equipment visuals. Each slot uses
   * its own descriptor's primaryColor/accentColor — no global armor
   * type/color. Only `resolved` slots produce geometry; empty, unknown,
   * missing, and mismatch all produce nothing. Each rendered layer
   * carries semantic data attributes (slot, item-id, family, variant).
   */
  static renderResolvedLayers(
    visuals: ResolvedPlayerEquipmentVisuals,
    anchors: GhostPoseAnchors = attachmentAnchorsForPose('idle'),
    context: GearFitContext = DEFAULT_GEAR_FIT,
  ): GearLayers {
    const limbScale = resolveLimbGearScale(context);
    const layers: GearLayers = {
      back: '',
      feet: '',
      backHand: '',
      backArm: '',
      body: '',
      frontArm: '',
      frontHand: '',
      head: '',
    };
    const bodyLayers: string[] = [];

    // Non-anchored slots: head, chest, legs, back, neck → silhouette registry.
    // head → head layer, chest+legs+neck → body layer, back → back layer.
    const headRes = visuals.head;
    if (headRes.kind === 'resolved') {
      const svg = renderResolvedSlotSilhouette(headRes.descriptor, context);
      if (svg) layers.head = fittedLayer(svg, 'head', context);
    }

    // Legs sit below the torso garment so tunic/robe hems own the overlap.
    const legsRes = visuals.legs;
    if (legsRes.kind === 'resolved') {
      const svg = renderResolvedSlotSilhouette(legsRes.descriptor, context);
      if (svg) bodyLayers.push(fittedLayer(svg, 'body', context));
    }

    const chestRes = visuals.chest;
    if (chestRes.kind === 'resolved') {
      const svg = renderResolvedSlotSilhouette(chestRes.descriptor, context);
      if (svg) {
        bodyLayers.push(fittedLayer(svg, 'body', context));
        const d = chestRes.descriptor;
        const sleeves = renderTorsoSleeves(
          d.family as TorsoFamily,
          d.primaryColor,
          d.accentColor ?? darken(d.primaryColor, 0.68),
          d.variant,
          context,
          anchors,
          `data-equipment-slot="${d.schemaSlot}" data-equipment-item-id="${d.itemId}" data-equipment-family="${d.family}" data-equipment-variant="${d.variant}"`,
        );
        layers.backArm = sleeves.back;
        layers.frontArm = sleeves.front;
      }
    }

    const neckRes = visuals.neck;
    if (neckRes.kind === 'resolved') {
      const svg = renderResolvedSlotSilhouette(neckRes.descriptor, context);
      if (svg) bodyLayers.push(fittedLayer(svg, 'body', context));
    }
    layers.body = bodyLayers.join('');

    const backRes = visuals.back;
    if (backRes.kind === 'resolved') {
      const svg = renderResolvedSlotSilhouette(backRes.descriptor, context);
      if (svg) layers.back = fittedLayer(svg, 'back', context);
    }

    // Anchored slots: feet → boots, hands → gloves.
    const feetRes = visuals.feet;
    if (feetRes.kind === 'resolved') {
      const d = feetRes.descriptor;
      const familyGeo = RESOLVED_APPENDAGE_FAMILIES[d.family];
      if (familyGeo) {
        const resolvedScale = limbScale * resolveAppendageVariantScale(d.variant);
        layers.feet =
          renderResolvedAppendage('boot', 'back-foot', anchors.backFoot, d, familyGeo.boot, resolvedScale, APPENDAGE_STYLE.backFootOpacity)
          + renderResolvedAppendage('boot', 'front-foot', anchors.frontFoot, d, familyGeo.boot, resolvedScale);
      }
    }

    const handsRes = visuals.hands;
    if (handsRes.kind === 'resolved') {
      const d = handsRes.descriptor;
      const familyGeo = RESOLVED_APPENDAGE_FAMILIES[d.family];
      if (familyGeo) {
        const resolvedScale = limbScale * resolveAppendageVariantScale(d.variant);
        layers.backHand = renderResolvedAppendage('glove', 'back-hand', anchors.backHand, d, familyGeo.glove, resolvedScale, APPENDAGE_STYLE.backHandOpacity);
        layers.frontHand = renderResolvedAppendage('glove', 'front-hand', anchors.frontHand, d, familyGeo.glove, resolvedScale);
      }
    }

    return layers;
  }
}
